import { firestoreService } from "@/services/firebase/firestore.service";
import { storageService } from "@/services/firebase/storage.service";
import { Form } from "@/types";
import { createAsyncThunk } from "@reduxjs/toolkit";

export interface AddFormWithFileParams {
  formData: Form
  document?: File
}

export const fetchForms = createAsyncThunk(
    'forms/fetchForms',
    async (_, {rejectWithValue})=>{
        try{
            const forms = await firestoreService.getDocuments('forms') as Form[]
            return forms
        } catch(error:any){
            return rejectWithValue(error.message || 'Failed to fetch forms')
        }
    }
)

export const addForm = createAsyncThunk(
    'forms/addForm',
    async (payload: Form, { rejectWithValue }) => {
        try {
            const docId = await firestoreService.addDocument('forms', payload) as string
            return { ...payload, id: docId }
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to add form')
        }
    }
)


export const addFormWithFile = createAsyncThunk<
  Form,
  AddFormWithFileParams,
  { rejectValue: string }
>(
  'forms/addFormWithFile',
  async ({ formData, document: doc }, { rejectWithValue }) => {
    try {
      let finalFormData = { ...formData }
      if (doc !== undefined) {
        try {
          const docPath = `forms/${doc.name}`
          await storageService.uploadFile(docPath, doc)
          finalFormData = { ...finalFormData, docUrl: doc.name }
        } catch (error: any) {
          return rejectWithValue(`Error al subir documento: ${error.message}`)
        }
      }
      try {
        const docId = await firestoreService.addDocument('forms', finalFormData) as string
        return { ...finalFormData, id: docId }
      } catch (error: any) {
        return rejectWithValue(`Error al guardar el formulario: ${error.message}`)
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add form with file')
    }
  }
)

export const updateForm = createAsyncThunk(
    'forms/updateForm',
    async ({ id, data }: { id: string; data: Partial<Form> }, { rejectWithValue }) => {
        try {
            await firestoreService.updateDocument('forms', id, data)
            return { id, ...data }
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update form')
        }
    }
)


export const updateFormWithFile = createAsyncThunk<
  Form,
  {
    id: string
    formData: Partial<Form>
    document?: File
  },
  { rejectValue: string }
>(
  'forms/updateFormWithFile',
  async ({ id, formData, document: doc }, { rejectWithValue }) => {
    try {
      let finalUpdateData = { ...formData }
      if (doc !== undefined) {
        try {
          // Get the existing form to check for old document
          const existingForm = await firestoreService.getDocument('forms', id) as Form | null

          // Delete old document if exists and is different from new one
          if (existingForm?.docUrl) {
            await storageService.deleteFile(`forms/${existingForm.docUrl}`)
          }

          // Upload new document
          const docPath = `forms/${doc.name}`
          await storageService.uploadFile(docPath, doc)
          finalUpdateData = { ...finalUpdateData, docUrl: doc.name }
        } catch (error: any) {
          return rejectWithValue(`Error al subir documento: ${error.message}`)
        }
      }
      try {
        await firestoreService.updateDocument('forms', id, finalUpdateData)
        return { id, ...finalUpdateData } as Form
      } catch (error: any) {
        return rejectWithValue(`Error al actualizar el formulario: ${error.message}`)
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update form with file')
    }
  }
)

export const fetchFormUrl = createAsyncThunk(
  'forms/fetchFormUrl',
  async ({ storagePath }: { storagePath: string }, { rejectWithValue }) => {
    try {
      // Build full storage path (forms/ + filename)
      const fullPath = `forms/${storagePath}`
      const downloadUrl = await storageService.getFileURL({ path: fullPath })
      return downloadUrl
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch form URL')
    }
  }
)

export const deleteForm = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'forms/deleteForm',
  async (id, { rejectWithValue }) => {
    try {
      // Get the form to check for associated files
      const form = await firestoreService.getDocument('forms', id) as Form | null

      if (!form) {
        return rejectWithValue('Formulario no encontrado')
      }

      // Delete associated document if exists
      if (form.docUrl) {
        await storageService.deleteFile(`forms/${form.docUrl}`)
      }

      // Delete the Firestore document
      await firestoreService.deleteDocument('forms', id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete form')
    }
  }
)